import { FC, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { CrossfadePanel } from '../../components/motion/index';
import { useScreenSize } from '../../hooks/index';
import { DataRoomTabs } from './components/data-room-tabs';
import { dataRoomModalTypeComponentMap } from './constants/data-room-modal-type-map.const';
import { DataRoomViewer, useDataRoomViewer } from './hooks/use-recipient-view.hook';
import { useResetOnViewerRoleChange } from './hooks/use-reset-on-viewer-role-change.hook';
import { DataRoomModalStore } from './store/data-room-modal.store';
import { SidebarTreeStore } from './store/sidebar-tree.store';
import { dataRoomTabFromPathname } from './utils/data-room-path.util';

export const DataRoomPage: FC = () => {
  const modalType = DataRoomModalStore(({ type }) => type);
  const { pathname } = useLocation();
  const selectedTab = dataRoomTabFromPathname(pathname);
  // Activity and Workspaces are the owner's tabs. A recipient has the room they were shared and
  // nothing around it, and while the role is unknown the tabs stay off — showing then hiding them
  // would be the same guess the view below refuses to make.
  const viewer = useDataRoomViewer();
  const { isMobile } = useScreenSize();
  const resetTree = SidebarTreeStore((state) => state.reset);

  useEffect(() => resetTree, [resetTree]);
  useResetOnViewerRoleChange();

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-md lg:shadow-sm">
      {viewer === DataRoomViewer.OWNER && !isMobile && <DataRoomTabs />}
      {/*
        No AnimatePresence around this one. CrossfadePanel is a plain div with a CSS entrance, so
        there is nothing to animate out — but `mode="wait"` still withholds the incoming panel
        until the outgoing one reports its exit, and the tab underlines are `layoutId` motion
        elements living in that outgoing tree, which leaves the report outstanding. The tab would
        change the URL and keep the previous route on screen. A new key remounts the panel and
        replays the entrance on its own, which is all this ever needed.
      */}
      <CrossfadePanel
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        key={selectedTab}
        panelKey={selectedTab}
        translate={false}
      >
        <Outlet />
      </CrossfadePanel>
      {dataRoomModalTypeComponentMap[modalType]}
    </div>
  );
};
